import { Box, HStack, IconButton, Input, Select, Text, Textarea, VStack } from "@chakra-ui/react";
import { Add, Close } from "@mui/icons-material";
import React from "react";

const ResponsePreferences = ({
    tone, setTone,
    answerLength, setAnswerLength,
    conversationValue, setConversationValue,
    isConversationEditedOnce, setIsConversationEditedOnce,
    tags, setTags,
    language, setLanguage,
    taskType
}) => {
    const allTones = [
        { emoji: "👔", name: "Professional"},
        { emoji: "😊", name: "Friendly" },
        { emoji: "⚖️", name: "Compliant" },
        { emoji: "✊", name: "Assertive" },
        { emoji: "👋", name: "Simplified" },
        { emoji: "😐", name: "Neutral" },
        { emoji: "🎯", name: "Persuasive" },
    ]

    const tagKeys = ["Business Unit", "Client Industry"];

    const tagValuesMap = {
        "Business Unit": ["Mining", "Buildings", "Civil"],
        "Client Industry": ["Public Services", "Education", "Healthcare", "Transportation", "Energy", "Construction", "Financial Services", "Technology", "Government", "Non-Profit"]

    };

    const allLengths = [
        {name: "Short"},
        {name: "Medium"},
        {name: "Long"},
    ]

    const handleToneClick = (tone) => {
        setTone(tone);
    }
    
    const isToneSelected = (t) => {
        return t.name === tone?.name;
    }

    const isLengthSelected = (l) => {
        return l.name === answerLength?.name;
    }

    const handleLengthClick = (length) => {
        setAnswerLength(length);
    }

    const hasRunOnceRef = React.useRef(false);

    const handleAddTag = () => {
        setTags((prev) => [...prev, {
            key: "",
            value: "",
        }]);
    }

    React.useEffect(() => {
        if (hasRunOnceRef.current || isConversationEditedOnce) return;
    
        hasRunOnceRef.current = true; // Prevent re-running
    
        let newConversationValue = "Hey Thalamus,";
    
        switch (taskType) {
            case "RFP":
                newConversationValue += " can you help draft responses for the attached RFP document.";
                break;
            case "proposal":
                newConversationValue += " can you help draft responses for the attached proposal document.";
                break;
            case "rfi":
                newConversationValue += " can you help draft responses for the attached RFI document.";
                break;
            case "search":
                newConversationValue += " Can you please search for the attached document.";
                break;
            default:
                newConversationValue += " can you help draft responses for the attached document.";
                break;
        }
    
        if (tone) {
            newConversationValue += ` Use a ${tone.name.toLowerCase()} tone.`;
        }
    
        if (answerLength) {
            newConversationValue += ` Aim for ${answerLength.name.toLowerCase()} length responses.`;
        }

        if (language) {
            newConversationValue += ` Please ensure to draft responses in ${language}.`;
        }
    
        const typingSpeed = 5;
    
        let oldConversationValue = conversationValue || "";
        let maxLength = Math.max(oldConversationValue.length, newConversationValue.length);
    
        let completedCharacters = 0;

        for (let i = 0; i < maxLength; i++) {
            setTimeout(() => {
            setConversationValue((prev) => {
                let updatedValue = prev;

                if (i < oldConversationValue.length && i < newConversationValue.length) {
                    if (oldConversationValue[i] !== newConversationValue[i]) {
                        updatedValue = updatedValue.slice(0, i) + newConversationValue[i] + updatedValue.slice(i + 1);
                    }
                } else if (i >= oldConversationValue.length) {
                    updatedValue += newConversationValue[i];
                } else if (i >= newConversationValue.length) {
                    updatedValue = updatedValue.slice(0, i);
                }

                completedCharacters++;
                if (completedCharacters === maxLength) {
                    hasRunOnceRef.current = false; // Reset after all characters are typed
                }

                return updatedValue;
            });
            }, i * typingSpeed);
        }
    
    }, [taskType, tone, answerLength, language]);
    

    return (
        <VStack
            width={"100%"}
            height={"100%"}
            spacing={0}
            alignItems={"flex-start"}
        >
            <Text fontSize={"16px"} fontWeight={600} color={"#333333"} lineHeight={"24px"}>
                Tone
            </Text>
            <HStack
                marginTop={"16px"}
                width={"100%"}
                wrap={"wrap"}
                gap={"16px"}
                rowGap={"16px"} // Added rowGap to set the gap between lines when wrapped
            >
                {
                    allTones.map((tone, index) => (
                        <HStack
                            key={index}
                            backgroundColor={isToneSelected(tone) ? "#E4F0FF" : "#F6F8FA"}
                            paddingX={"16px"}
                            paddingY={"8px"}
                            borderRadius={"8px"}
                            gap={"4px"}
                            _hover={{
                                backgroundColor: isToneSelected(tone) ? "#E4F0FF" : "#E4F0FF",
                                cursor: "pointer",
                            }}
                            onClick={() => handleToneClick(tone)}
                        >
                            <Text>
                                {tone.emoji}
                            </Text>
                            <Text>
                                {tone.name}
                            </Text>
                        </HStack>
                    ))
                }
            </HStack>
            <Text
                fontSize={"16px"}
                fontWeight={600}
                color={"#333333"}
                marginTop={"24px"}
                lineHeight={"24px"}
            >
                Length of answer
            </Text>
            <HStack
                marginTop={"16px"}
                width={"100%"}
                wrap={"wrap"}
                gap={"16px"}
                rowGap={"16px"} // Added rowGap to set the gap between lines when wrapped
            >
                {
                    allLengths.map((length, index) => (
                        <HStack
                            key={index}
                            backgroundColor={isLengthSelected(length) ? "#E4F0FF" : "#F6F8FA"}
                            paddingX={"16px"}
                            paddingY={"8px"}
                            borderRadius={"8px"}
                            gap={"4px"}
                            _hover={{
                                backgroundColor: isLengthSelected(length) ? "#E4F0FF" : "#E4F0FF",
                                cursor: "pointer",
                            }}
                            onClick={() => handleLengthClick(length)}
                        >
                            <Text>
                                {length.name}
                            </Text>
                        </HStack>
                    ))
                }
            </HStack>
            <Text
                fontSize={"16px"}
                fontWeight={600}
                color={"#333333"}
                marginTop={"24px"}
                lineHeight={"24px"}
            >
                Language
            </Text>
            <Select
                placeholder="Select language"
                marginTop={"16px"}
                width={"240px"}
                height={"40px"}
                borderRadius={"8px"}
                borderColor={"#ADADAD"}
                color={language ? "#1D1D1D" : "#6B7C93"}
                fontSize={"16px"}
                fontWeight={400}
                value={language} // Set default value to English
                onChange={(e) => setLanguage(e.target.value)}
            >
                <option value="english">English</option>
                <option value="french">French</option>
                <option value="spanish">Spanish</option>
                <option value={"arabic"}>Arabic</option>
                <option value="german">German</option>
                <option value={"italian"}>Italian</option>
                <option value={"portuguese"}>Portuguese</option>
                <option value={"chinese"}>Chinese</option>
                <option value={"japanese"}>Japanese</option>
                <option value={"korean"}>Korean</option>
                <option value={"russian"}>Russian</option>
                <option value={"hindi"}>Hindi</option>
                <option value={"bengali"}>Bengali</option>
                <option value={"turkish"}>Turkish</option>
            </Select>
            <Text
                fontSize={"16px"}
                fontWeight={600}
                color={"#333333"}
                marginTop={"24px"}
                lineHeight={"24px"}
            >
                Prompt
            </Text>
            <Box
                marginTop={"16px"}
                width={"100%"}
                padding={"16px 24px"}
                backgroundColor={"#F6F8FA"}
                borderRadius={"16px"}
                border={"1px solid #E4E4E4"}
            >
                <Textarea
                    value={conversationValue}
                    onChange={(e) => {
                        setIsConversationEditedOnce(true)
                        setConversationValue(e.target.value)
                    }}
                    style={{
                        width: "100%",
                        height: "100px",
                        borderRadius: "8px",
                        backgroundColor: "white",
                        padding: "8px",
                        fontSize: "16px",
                        fontFamily: "inherit",
                        color: "#333333",
                        resize: "none",
                    }}
                />
            </Box>
            <Text
                fontSize={"16px"}
                fontWeight={600}
                color={"#333333"}
                marginTop={"24px"}
                lineHeight={"24px"}
            >
                Sources
            </Text>
            <Select
                placeholder="Select sources"
                marginTop={"16px"}
                width={"240px"}
                height={"40px"}
                borderRadius={"8px"}
                borderColor={"#ADADAD"}
                color={"#6B7C93"}
                fontSize={"16px"}
                fontWeight={400}
            >
            </Select>
            <Text
                fontSize={"16px"}
                fontWeight={600}
                color={"#333333"}
                marginTop={"24px"}
                lineHeight={"24px"}
            >
                Tags
            </Text>
            {
                tags?.map((tag, index) => (
                    <HStack
                        key={index}
                        marginTop={"16px"}
                        width={"100%"}
                        borderRadius={"8px"}
                        gap={"4px"}
                        height={"40px"}
                        position="relative"
                    >
                        <Input
                            placeholder="Key"
                            size="lg"
                            fontSize={"14px"}
                            width={"30%"}
                            height={"40px"}
                            value={tag.key}
                            paddingX={"16px"}
                            paddingY={"12px"}
                            onChange={(e) => {
                                setTags((prev) => {
                                    const newTags = [...prev];
                                    newTags[index].key = e.target.value;
                                    return newTags;
                                });
                            }}
                            onFocus={() => {
                                setTags((prev) => {
                                    const newTags = [...prev];
                                    if (newTags[index]) {
                                        newTags[index].showKeyDropdown = true;
                                    }
                                    return newTags;
                                });
                            }}
                            onBlur={() => {
                                setTimeout(() => {
                                    setTags((prev) => {
                                        const newTags = [...prev];
                                        if (newTags[index]) {
                                            newTags[index].showKeyDropdown = false;
                                        }
                                        return newTags;
                                    });
                                }, 200); // Delay to allow click on dropdown
                            }}
                            _placeholder={{ color: "#A0A0A0" }}
                        />
                        {tag.showKeyDropdown && (
                            <Box
                                position="absolute"
                                bottom="110%" // Changed from top="100%" to bottom="100%"
                                left="0"
                                width="30%"
                                backgroundColor="white"
                                border="1px solid #E4E4E4"
                                borderRadius="8px"
                                zIndex="10"
                                maxHeight="150px"
                                overflowY="auto"
                                data-scrollable="true"
                            >
                                {tagKeys
                                    .filter((option) =>
                                        option.toLowerCase().includes(tag.key.toLowerCase())
                                    )
                                    .map((filteredOption, idx) => (
                                        <Box
                                            key={idx}
                                            padding="8px"
                                            _hover={{ backgroundColor: "#F6F8FA", cursor: "pointer" }}
                                            onClick={() => {
                                                setTags((prev) => {
                                                    const newTags = [...prev];
                                                    newTags[index].key = filteredOption;
                                                    newTags[index].showKeyDropdown = false;
                                                    return newTags;
                                                });
                                            }}
                                        >
                                            {filteredOption}
                                        </Box>
                                    ))}
                            </Box>
                        )}
                        <Input
                            placeholder="Value"
                            size="lg"
                            fontSize={"14px"}
                            width={"40%"}
                            height={"40px"}
                            value={tag.value}
                            paddingX={"16px"}
                            paddingY={"12px"}
                            onChange={(e) => {
                                setTags((prev) => {
                                    const newTags = [...prev];
                                    newTags[index].value = e.target.value;
                                    return newTags;
                                });
                            }}
                            onFocus={() => {
                                setTags((prev) => {
                                    const newTags = [...prev];
                                    if (newTags[index]) {
                                        newTags[index].showValueDropdown = true;
                                    }
                                    return newTags;
                                });
                            }}
                            onBlur={() => {
                                setTimeout(() => {
                                    setTags((prev) => {
                                        const newTags = [...prev];
                                        if (newTags[index]) {
                                            newTags[index].showValueDropdown = false;
                                        }
                                        return newTags;
                                    });
                                }, 200); // Delay to allow click on dropdown
                            }}
                            _placeholder={{ color: "#A0A0A0" }}
                        />
                        {tag.showValueDropdown && (
                            <Box
                                position="absolute"
                                bottom="110%" // Changed from top="100%" to bottom="100%"
                                left={`calc(30% + 8px)`}
                                width="40%"
                                backgroundColor="white"
                                border="1px solid #E4E4E4"
                                borderRadius="8px"
                                zIndex="10"
                                maxHeight="150px"
                                overflowY="auto"
                                data-scrollable="true"
                            >
                                {(() => {
                                    const optionsToShow = tagValuesMap[tag.key] || [];

                                    return optionsToShow.filter((option) =>
                                        option.toLowerCase().includes(tag.value.toLowerCase())
                                    ).map((filteredOption, idx) => (
                                        <Box
                                            key={idx}
                                            padding="8px"
                                            _hover={{ backgroundColor: "#F6F8FA", cursor: "pointer" }}
                                            onClick={() => {
                                                setTags((prev) => {
                                                    const newTags = [...prev];
                                                    newTags[index].value = filteredOption;
                                                    newTags[index].showValueDropdown = false;
                                                    return newTags;
                                                });
                                            }}
                                        >
                                            {filteredOption}
                                        </Box>
                                    ));
                                })()}
                            </Box>
                        )}
                        <IconButton
                            sx={{
                                padding: "0px",
                                margin: "0px",
                                "&:hover": {
                                    backgroundColor: "transparent",
                                },
                                backgroundColor: "transparent",
                                fontSize: "24px",
                                minW: "unset",
                            }}
                            onClick={() => {
                                setTags((prev) => prev.filter((_, i) => i !== index));
                            }}
                        >
                            <Close
                                sx={{
                                    padding: "0px",
                                    margin: "0px",
                                    color: "#5d5d5d",
                                    fontSize: "22px",
                                }}
                            />
                        </IconButton>
                    </HStack>
                ))
            }
            <HStack
                gap={"4px"}
                onClick={() => handleAddTag()}
                _hover={{
                    cursor: "pointer",
                }}
                marginTop={"16px"}
            >
                <IconButton
                    sx={{
                        padding: "0px",
                        margin: "0px",
                        "&:hover": {
                            backgroundColor: "transparent",
                        },
                        backgroundColor: "transparent",
                        fontSize: "24px",
                        minW: "unset",
                    }}
                >
                    <Add
                        sx={{
                            padding: "0px",
                            margin: "0px",
                            color: "#2584FD",
                            fontSize: "22px",
                        }}
                    />
                </IconButton>
                <Text
                    fontSize={"16px"}
                    fontWeight={500}
                    color="#2584FD"
                >
                    Add Tag
                </Text>
            </HStack>
        </VStack>
    )
}

export default ResponsePreferences;